/**
 * Auth State Machine — single source of truth for all auth state.
 *
 * Replaces the scattered auth logic across AuthProvider, RequireAuth,
 * UserChangeGuard, and ConvexErrorBoundary with a single reducer-based
 * state machine.
 *
 * Exports:
 *  - AuthMachineProvider — wraps the app, manages auth lifecycle
 *  - useAuth()           — backward-compatible hook (same shape as old AuthProvider)
 *  - useAuthDispatch()   — dispatch login/logout actions (for login page)
 *  - useConvexAuth()     — hook for ConvexProviderWithAuth
 *  - useSessionId()      — user.id for key={} remount pattern
 *  - AuthGate            — renders children only when authenticated
 */
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { useLocation } from "wouter";
import { isNativeApp } from "@/lib/capacitor";
import { clearSessionData, getLastUserId, setLastUserId, getUserStorage } from "@/lib/user-storage";
import { AuthTransitionLoader } from "@/components/auth-transition-loader";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  imageUrl: string | null;
}

export type AuthStatus =
  | "initializing"     // Checking session on mount
  | "unauthenticated"  // No valid session
  | "authenticating"   // Login in progress
  | "authenticated"    // Session valid, user known
  | "error";           // Login failed (transient)

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
}

export type AuthAction =
  | { type: "SESSION_VALID"; user: AuthUser }
  | { type: "SESSION_INVALID" }
  | { type: "LOGIN_START" }
  | { type: "LOGIN_SUCCESS"; user: AuthUser }
  | { type: "LOGIN_FAILED"; error: string }
  | { type: "CLEAR_ERROR" }
  | { type: "LOGOUT" };

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case "SESSION_VALID":
      return { status: "authenticated", user: action.user, error: null };

    case "SESSION_INVALID":
      return { status: "unauthenticated", user: null, error: null };

    case "LOGIN_START":
      return { ...state, status: "authenticating", error: null };

    case "LOGIN_SUCCESS":
      return { status: "authenticated", user: action.user, error: null };

    case "LOGIN_FAILED":
      return { status: "error", user: null, error: action.error };

    case "CLEAR_ERROR":
      return { ...state, status: "unauthenticated", error: null };

    case "LOGOUT":
      return { status: "unauthenticated", user: null, error: null };

    default:
      return state;
  }
}

const INITIAL_STATE: AuthState = {
  status: "initializing",
  user: null,
  error: null,
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

/** Backward-compatible context shape (matches old AuthProvider) */
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  isDevBypassMode: boolean;
  user: AuthUser | null;
  signOut: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
}

type DispatchFn = (action: AuthAction) => void;

const AuthStateContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  isDevBypassMode: false,
  user: null,
  signOut: async () => {},
  refreshAuth: async () => false,
});

const AuthDispatchContext = createContext<DispatchFn>(() => {});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthMachineProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, INITIAL_STATE);
  const prevUserIdRef = useRef<string | null>(null);

  // --- Session check on mount ---
  const checkSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await fetch("/api/auth/me", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const user: AuthUser = {
          id: data.user.id,
          firstName: data.user.firstName,
          lastName: data.user.lastName,
          email: data.user.email,
          imageUrl: data.user.imageUrl,
        };
        dispatch({ type: "SESSION_VALID", user });
        return true;
      } else {
        dispatch({ type: "SESSION_INVALID" });
        return false;
      }
    } catch {
      dispatch({ type: "SESSION_INVALID" });
      return false;
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // --- User change detection (replaces UserChangeGuard) ---
  useEffect(() => {
    if (state.status !== "authenticated" || !state.user) return;

    const currentId = state.user.id;
    const lastId = getLastUserId();

    // Different user than what's stored — clear old user's data
    if (lastId && lastId !== currentId) {
      console.log("[AuthMachine] User changed:", lastId, "→", currentId);
      clearSessionData(lastId);
    }

    // Track current user
    setLastUserId(currentId);
    prevUserIdRef.current = currentId;
  }, [state.status, state.user]);

  // --- Logout on auth loss (clear stored data) ---
  useEffect(() => {
    if (state.status === "unauthenticated") {
      const lastId = getLastUserId();
      if (lastId && prevUserIdRef.current) {
        console.log("[AuthMachine] Logged out, clearing session data");
        clearSessionData(lastId);
        localStorage.removeItem("shiftcanvas_last_user_id");
        prevUserIdRef.current = null;
      }
    }
  }, [state.status]);

  // Remove this device's push-token binding for the signing-out user. Must run
  // while the session is still valid (the mutation requires auth). Without it,
  // on a shared tablet the previous user's notifications keep arriving after
  // someone else takes over the device.
  const unbindPushToken = useCallback(async (userId: string | null) => {
    try {
      const fcmToken = getUserStorage(userId).getFcmToken();
      const convexUrl = import.meta.env.VITE_CONVEX_URL;
      if (!fcmToken || !convexUrl) return;
      const tokRes = await fetch("/api/auth/convex-token", { credentials: "include" });
      if (!tokRes.ok) return;
      const { token } = await tokRes.json();
      if (!token) return;
      const { ConvexHttpClient } = await import("convex/browser");
      const client = new ConvexHttpClient(convexUrl);
      client.setAuth(token);
      await client.mutation("notifications:unregisterDeviceToken" as any, { token: fcmToken });
      console.log("[AuthMachine] Push token unbound for signing-out user");
    } catch (err) {
      console.warn("[AuthMachine] Failed to unbind push token:", err);
    }
  }, []);

  // --- signOut ---
  const signOut = useCallback(async () => {
    const lastId = getLastUserId();
    await unbindPushToken(lastId);
    clearSessionData(lastId);
    localStorage.removeItem("shiftcanvas_last_user_id");

    if (isNativeApp()) {
      dispatch({ type: "LOGOUT" });
      // Clear the NATIVE Google session first (before any network wait):
      // GIDSignIn caches its account until something calls signOut, and
      // prompt=login only covers the Kinde browser flows — without this, the
      // next person to tap Google lands straight in the previous account.
      // Apple has no equivalent persistent session to clear.
      try {
        const { SocialLogin } = await import("@capgo/capacitor-social-login");
        await SocialLogin.logout({ provider: "google" } as any);
      } catch {
        // Best-effort — plugin may be uninitialized on this platform
      }
      try {
        // Destroys the server session. No browser round-trip: the old Kinde
        // SSO-clearing sheet could strand users (SFSafariViewController won't
        // open the presenting app's own scheme, and a close-timer dies when
        // the navigation below tears down this JS context). Fresh logins ask
        // for credentials anyway — the native authorize URL sends
        // prompt=login — so clearing SSO here buys nothing.
        await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
      } catch {
        // Best-effort session destroy
      }
      window.location.href = "/app/login";
    } else {
      // Redirect immediately — dispatching LOGOUT first would cause a white flash
      // before the full-page navigation completes
      window.location.href = "/logout";
    }
  }, [unbindPushToken]);

  // --- Derived context value (backward compatible) ---
  const contextValue: AuthContextType = {
    isAuthenticated: state.status === "authenticated",
    isLoading: state.status === "initializing",
    isDevBypassMode: false,
    user: state.user,
    signOut,
    refreshAuth: checkSession,
  };

  return (
    <AuthDispatchContext.Provider value={dispatch}>
      <AuthStateContext.Provider value={contextValue}>
        {children}
      </AuthStateContext.Provider>
    </AuthDispatchContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Backward-compatible auth hook — same shape as old AuthProvider */
export function useAuth() {
  return useContext(AuthStateContext);
}

/** Dispatch auth actions (LOGIN_START, LOGIN_SUCCESS, etc.) */
export function useAuthDispatch() {
  return useContext(AuthDispatchContext);
}

/** Auth status for fine-grained checks */
export function useAuthStatus(): AuthStatus {
  const { isAuthenticated, isLoading } = useContext(AuthStateContext);
  if (isLoading) return "initializing";
  if (isAuthenticated) return "authenticated";
  return "unauthenticated";
}

/** User ID for key={} remount pattern */
export function useSessionId(): string | null {
  const { user } = useContext(AuthStateContext);
  return user?.id ?? null;
}

/**
 * Hook for ConvexProviderWithAuth.
 * Returns { isLoading, isAuthenticated, fetchAccessToken }.
 *
 * fetchAccessToken fetches from /api/auth/convex-token.
 * On 401+expired, dispatches LOGOUT to the auth machine (no direct redirect).
 */
export function useConvexAuth() {
  const { isAuthenticated, isLoading } = useContext(AuthStateContext);
  const dispatch = useContext(AuthDispatchContext);

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const res = await fetch("/api/auth/convex-token", {
            credentials: "include",
          });

          if (res.status === 401) {
            const data = await res.json().catch(() => ({}));
            if (data.expired) {
              console.log("[ConvexAuth] Token expired, dispatching logout");
              dispatch({ type: "LOGOUT" });
              // Force navigation to login
              if (isNativeApp()) {
                window.location.href = "/app/login";
              } else {
                window.location.href = "/logout";
              }
            }
            return null;
          }

          if (!res.ok) {
            if (attempt === 0) {
              console.warn("[ConvexAuth] Token fetch failed (status", res.status, "), retrying...");
              await new Promise((r) => setTimeout(r, 100));
              continue;
            }
            return null;
          }

          const data = await res.json();
          return data.token || null;
        } catch {
          if (attempt === 0) {
            console.warn("[ConvexAuth] Token fetch error, retrying...");
            await new Promise((r) => setTimeout(r, 100));
            continue;
          }
          return null;
        }
      }
      return null;
    },
    [dispatch],
  );

  return {
    isLoading,
    isAuthenticated,
    fetchAccessToken,
  };
}

// ---------------------------------------------------------------------------
// AuthGate — replaces RequireAuth + UserChangeGuard
// ---------------------------------------------------------------------------

const PRIMARY_LOGIN_URL = "/login";

/**
 * Renders children only when authenticated.
 * When unauthenticated:
 *  - Native: navigates to /app/login (client-side)
 *  - Web: redirects to /login?redirect_url=... (server-side OAuth)
 *
 * Replaces RequireAuth and UserChangeGuard.
 */
export function AuthGate({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const [, navigate] = useLocation();
  const redirected = useRef(false);

  useEffect(() => {
    if (isLoading || isAuthenticated || redirected.current) return;

    if (typeof window === "undefined") return;

    if (isNativeApp()) {
      redirected.current = true;
      navigate("/app/login");
    } else {
      redirected.current = true;
      const currentUrl = window.location.href;
      const encodedReturnUrl = encodeURIComponent(currentUrl);
      window.location.href = `${PRIMARY_LOGIN_URL}?redirect_url=${encodedReturnUrl}`;
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Reset redirect flag when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      redirected.current = false;
    }
  }, [isAuthenticated]);

  if (isLoading || !isAuthenticated) {
    return fallback ? <>{fallback}</> : <AuthTransitionLoader />;
  }

  return <>{children}</>;
}
